import { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import { uploadData } from 'aws-amplify/storage';
import { v4 as uuidv4 } from 'uuid';
import { authClient } from '../../utils/amplifyClient';
import { useSafetyTrainingContext } from '../../contexts/SafetyTrainingContext';
import { useUserContext } from '../../contexts/UserContext';
import {
    TopicStyle,
    TOPIC_STYLE_COLORS,
    PlanCategory,
    TrainingTopic,
} from '../../types/trainingTypes';
import { listDocuments as listDocumentsQuery, listVideos as listVideosQuery } from '../../graphql/queries';
import { createCompanyResource, updateCompanyResource } from '../../graphql/companyResourceQueries';
import { updateTrainingTopic as updateTrainingTopicGql } from '../../graphql/trainingQueries';
import '../../css/AddEditTopicModal.css';

interface AddEditTopicModalProps {
    isOpen: boolean;
    onClose: () => void;
    editTopicId?: string | null;
}

const STYLE_OPTIONS: { value: TopicStyle; label: string }[] = [
    { value: 'Video', label: 'Video' },
    { value: 'PDF', label: 'PDF' },
    { value: 'Poster', label: 'Poster' },
    { value: 'FatalGram', label: 'FatalGram' },
    { value: 'Other', label: 'Other' },
    { value: 'Uncategorized', label: 'Uncategorized' },
];

const ACCEPTED_EXTENSIONS = ['.mp4', '.mov', '.pdf', '.jpg', '.jpeg', '.png'];
const ACCEPTED_MIME_TYPES = [
    'video/mp4',
    'video/quicktime',
    'application/pdf',
    'image/jpeg',
    'image/png',
];
const MAX_FILE_SIZE_BYTES = 2 * 1024 * 1024 * 1024; // 2 GB
const SLOW_NETWORK_THRESHOLD_MBPS = 5; // Warn if connection speed is below 5 Mbps
const LARGE_FILE_THRESHOLD_BYTES = 100 * 1024 * 1024; // Show network warning for files > 100 MB

/**
 * AddEditTopicModal — Create or edit a training topic.
 *
 * Validates: Requirements 2.1, 2.2, 2.3, 2.4, 2.5, 2.6, 2.7, 2.8, 2.9, 2.10, 2.11
 */
export const AddEditTopicModal = ({ isOpen, onClose, editTopicId }: AddEditTopicModalProps) => {
    const { topics, plans, schedules, createTopic, updateTopic } = useSafetyTrainingContext();
    const { companyUser, companyOwner, businessAdmin } = useUserContext();
    const userCompanyId = (companyUser?.companyId as string) || companyOwner?.companyId || '';
    const isTemplateMode = !userCompanyId && !!businessAdmin;
    const companyId = isTemplateMode ? 'library-template' : userCompanyId;

    const isEditing = Boolean(editTopicId);
    const existingTopic: TrainingTopic | undefined = useMemo(
        () => (editTopicId ? topics.find(t => t.id === editTopicId) : undefined),
        [editTopicId, topics],
    );

    // ---- Form state ----
    const [style, setStyle] = useState<TopicStyle>('Video');
    const [topicIdValue, setTopicIdValue] = useState('');
    const [timeValue, setTimeValue] = useState<number>(0);
    const [description, setDescription] = useState('');
    const [contentTab, setContentTab] = useState<'upload' | 'url' | 'library'>('upload');
    const [contentUrl, setContentUrl] = useState('');
    const [selectedFile, setSelectedFile] = useState<File | null>(null);
    const [existingFilePath, setExistingFilePath] = useState<string | null>(null);
    const [selectedCategoryIds, setSelectedCategoryIds] = useState<Set<string>>(new Set());
    const [isDragOver, setIsDragOver] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [uploadProgress, setUploadProgress] = useState<number | null>(null);
    const [errors, setErrors] = useState<Record<string, string>>({});
    const [savingError, setSavingError] = useState<string | null>(null);
    const [saveToCompanyResources, setSaveToCompanyResources] = useState(false);

    // ---- Resource Library import state ----
    const [libraryItems, setLibraryItems] = useState<{ id: string; title: string; type: 'Document' | 'Video'; contentPath: string; folderName: string }[]>([]);
    const [libraryLoading, setLibraryLoading] = useState(false);
    const [libraryLoaded, setLibraryLoaded] = useState(false);
    const [librarySearch, setLibrarySearch] = useState('');

    const fileInputRef = useRef<HTMLInputElement>(null);

    // ---- Parse categories grouped by plan ----
    const categoriesByPlan = useMemo(() => {
        const result: { plan: { id: string; name: string; status: string }; categories: PlanCategory[] }[] = [];
        plans.forEach(plan => {
            try {
                const parsed: PlanCategory[] = JSON.parse(plan.categories);
                if (parsed.length > 0) {
                    result.push({
                        plan: { id: plan.id, name: plan.name, status: plan.status },
                        categories: parsed,
                    });
                }
            } catch { /* ignore */ }
        });
        return result;
    }, [plans]);

    // Category IDs that are locked (belong to active plans where this topic is scheduled)
    // These cannot be unchecked to prevent removing a topic from an active plan's category
    const lockedCategoryIds = useMemo(() => {
        if (!isEditing || !existingTopic) return new Set<string>();
        const activePlanIds = new Set(plans.filter(p => p.status === 'ACTIVE').map(p => p.id));
        // Check if this topic is scheduled in any active plan
        const scheduledActivePlanIds = new Set(
            schedules
                .filter(s => s.topicId === existingTopic.id && activePlanIds.has(s.planId))
                .map(s => s.planId)
        );
        if (scheduledActivePlanIds.size === 0) return new Set<string>();
        // Lock all categories that belong to those active plans
        const locked = new Set<string>();
        categoriesByPlan.forEach(({ plan, categories }) => {
            if (scheduledActivePlanIds.has(plan.id)) {
                categories.forEach(c => locked.add(c.id));
            }
        });
        return locked;
    }, [isEditing, existingTopic, plans, schedules, categoriesByPlan]);

    // Track which plan groups are expanded in the category section
    const [expandedPlanIds, setExpandedPlanIds] = useState<Set<string>>(new Set());

    const togglePlanExpanded = useCallback((planId: string) => {
        setExpandedPlanIds(prev => {
            const next = new Set(prev);
            if (next.has(planId)) next.delete(planId);
            else next.add(planId);
            return next;
        });
    }, []);

    // ---- Fetch Resource Library items (lazy — only when tab is selected) ----
    const fetchLibraryItems = useCallback(async () => {
        if (libraryLoaded || libraryLoading || !companyId) return;
        setLibraryLoading(true);
        try {
            const [docsRes, vidsRes] = await Promise.all([
                authClient.graphql({ query: listDocumentsQuery }) as any,
                authClient.graphql({ query: listVideosQuery }) as any,
            ]);
            const docs = (docsRes.data?.listDocuments?.items ?? [])
                .filter((d: any) => d.sharedWithCompanyIds?.includes(companyId) && d.contentPath)
                .map((d: any) => ({
                    id: d.id,
                    title: d.title || d.contentPath?.split('/').pop() || 'Untitled Document',
                    type: 'Document' as const,
                    contentPath: d.contentPath,
                    folderName: d.folderName || '',
                }));
            const vids = (vidsRes.data?.listVideos?.items ?? [])
                .filter((v: any) => v.sharedWithCompanyIds?.includes(companyId) && v.contentPath)
                .map((v: any) => ({
                    id: v.id,
                    title: v.title || v.contentPath?.split('/').pop() || 'Untitled Video',
                    type: 'Video' as const,
                    contentPath: v.contentPath,
                    folderName: v.folderName || '',
                }));
            setLibraryItems([...docs, ...vids]);
            setLibraryLoaded(true);
        } catch (err) {
            console.error('Failed to fetch resource library:', err);
        } finally {
            setLibraryLoading(false);
        }
    }, [companyId, libraryLoaded, libraryLoading]);

    useEffect(() => {
        if (contentTab === 'library' && !libraryLoaded && !libraryLoading) {
            fetchLibraryItems();
        }
    }, [contentTab, libraryLoaded, libraryLoading, fetchLibraryItems]);

    const filteredLibraryItems = useMemo(() => {
        if (!librarySearch.trim()) return libraryItems;
        const q = librarySearch.toLowerCase();
        return libraryItems.filter(item =>
            item.title.toLowerCase().includes(q) || item.folderName.toLowerCase().includes(q)
        );
    }, [libraryItems, librarySearch]);

    const handleSelectLibraryItem = useCallback((item: typeof libraryItems[0]) => {
        const ext = item.contentPath.split('.').pop()?.toLowerCase() || '';
        let autoStyle: TopicStyle = 'Other';
        if (item.type === 'Video' || ['mp4', 'mov'].includes(ext)) autoStyle = 'Video';
        else if (ext === 'pdf') autoStyle = 'PDF';
        else if (['jpg', 'jpeg', 'png'].includes(ext)) autoStyle = 'Poster';
        setStyle(autoStyle);
        setDescription(item.title);
        setExistingFilePath(item.contentPath);
        setSelectedFile(null);
        setContentUrl('');
        setContentTab('upload');
    }, []);

    // ---- Pre-populate on edit ----
    useEffect(() => {
        if (isEditing && existingTopic) {
            setStyle(existingTopic.style);
            setTopicIdValue(existingTopic.topicId);
            setTimeValue(existingTopic.timeValue);
            setDescription(existingTopic.description);
            setContentUrl(existingTopic.contentUrl || '');
            setExistingFilePath(existingTopic.contentFilePath || null);
            setSelectedFile(null);
            setSelectedCategoryIds(new Set(existingTopic.categoryIds || []));
            setContentTab(existingTopic.contentUrl ? 'url' : 'upload');
        } else {
            // Reset for new topic
            setStyle('Video');
            setTopicIdValue('');
            setTimeValue(0);
            setDescription('');
            setContentUrl('');
            setExistingFilePath(null);
            setSelectedFile(null);
            setSelectedCategoryIds(new Set());
            setContentTab('upload');
        }
        setErrors({});
        setSavingError(null);
        setUploadProgress(null);
        setIsSaving(false);
        setSaveToCompanyResources(false);
    }, [isEditing, existingTopic, isOpen]);

    // ---- Auto-set time to 0 when Uncategorized ----
    useEffect(() => {
        if (style === 'Uncategorized') {
            setTimeValue(0);
        }
    }, [style]);

    // ---- Validation ----
    const validate = useCallback((): boolean => {
        const newErrors: Record<string, string> = {};

        if (!topicIdValue.trim()) {
            newErrors.topicId = 'Topic ID is required.';
        }

        if (!description.trim()) {
            newErrors.description = 'Description is required.';
        }

        if (selectedFile && selectedFile.size > MAX_FILE_SIZE_BYTES) {
            newErrors.file = 'File exceeds 2 GB limit.';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    }, [topicIdValue, description, isEditing, topics, selectedFile]);

    // ---- Network speed check helper ----
    const getNetworkWarning = useCallback((fileSize: number): string | null => {
        if (fileSize <= LARGE_FILE_THRESHOLD_BYTES) return null;
        const connection = (navigator as any).connection;
        if (connection && connection.downlink && connection.downlink < SLOW_NETWORK_THRESHOLD_MBPS) {
            return (
                `Your network speed appears slow (~${connection.downlink} Mbps). ` +
                `Uploading ${(fileSize / (1024 * 1024)).toFixed(0)} MB may take a while. Consider using a stronger connection or a smaller file.`
            );
        }
        return null;
    }, []);

    const [networkWarning, setNetworkWarning] = useState<string | null>(null);

    // ---- File handling ----
    const handleFileSelect = useCallback((file: File) => {
        const ext = '.' + file.name.split('.').pop()?.toLowerCase();
        if (!ACCEPTED_EXTENSIONS.includes(ext) && !ACCEPTED_MIME_TYPES.includes(file.type)) {
            setErrors(prev => ({ ...prev, file: 'Unsupported file type. Accepted: MP4, MOV, PDF, JPG, PNG.' }));
            return;
        }
        if (file.size > MAX_FILE_SIZE_BYTES) {
            setErrors(prev => ({ ...prev, file: 'File exceeds 2 GB limit.' }));
            return;
        }
        // Check network speed for large files
        setNetworkWarning(getNetworkWarning(file.size));
        setSelectedFile(file);
        setErrors(prev => {
            const next = { ...prev };
            delete next.file;
            return next;
        });
    }, [getNetworkWarning]);

    const handleDrop = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(false);
        const file = e.dataTransfer.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);

    const handleDragOver = useCallback((e: React.DragEvent) => {
        e.preventDefault();
        setIsDragOver(true);
    }, []);

    const handleDragLeave = useCallback(() => {
        setIsDragOver(false);
    }, []);

    const handleFileInputChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        if (file) handleFileSelect(file);
    }, [handleFileSelect]);

    const handleRemoveFile = useCallback(() => {
        setSelectedFile(null);
        setExistingFilePath(null);
        setNetworkWarning(null);
        if (fileInputRef.current) fileInputRef.current.value = '';
    }, []);

    // ---- Category toggle ----
    const toggleCategory = useCallback((catId: string) => {
        // Don't allow unchecking locked categories (active plan protection)
        if (lockedCategoryIds.has(catId) && selectedCategoryIds.has(catId)) return;
        setSelectedCategoryIds(prev => {
            const next = new Set(prev);
            if (next.has(catId)) next.delete(catId);
            else next.add(catId);
            return next;
        });
    }, [lockedCategoryIds, selectedCategoryIds]);

    // ---- Format file size ----
    const formatFileSize = (bytes: number): string => {
        if (bytes < 1024) return `${bytes} B`;
        if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
        return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    };

    // ---- Save ----
    const handleSave = useCallback(async () => {
        if (!validate()) return;
        setIsSaving(true);
        setSavingError(null);

        try {
            let filePath: string | null = existingFilePath;

            // Upload file to S3 if a new file is selected
            if (selectedFile) {
                const fileName = `${uuidv4()}-TOPIC-${selectedFile.name}`;
                const s3Path = `public/trainingTopics/${companyId}/${fileName}`;
                const uploadTask = uploadData({
                    path: s3Path,
                    data: selectedFile,
                    options: {
                        onProgress: (progress) => {
                            if (progress.totalBytes) {
                                setUploadProgress(
                                    Math.round((progress.transferredBytes / progress.totalBytes) * 100),
                                );
                            }
                        },
                    },
                });
                await uploadTask.result;
                filePath = s3Path;
            }

            const categoryIdsArray = Array.from(selectedCategoryIds);
            const finalContentUrl = contentTab === 'url' ? contentUrl.trim() || null : null;
            const finalFilePath = (contentTab === 'upload' || contentTab === 'library') ? filePath : null;

            // Derive planIds from selected categories — a topic belongs to every plan
            // that owns at least one of its selected categories
            const derivedPlanIds = new Set<string>();
            categoriesByPlan.forEach(({ plan, categories: planCats }) => {
                if (planCats.some(c => selectedCategoryIds.has(c.id))) {
                    derivedPlanIds.add(plan.id);
                }
            });
            const planIdsArray = Array.from(derivedPlanIds);

            if (isEditing && existingTopic) {
                await updateTopic({
                    id: existingTopic.id,
                    topicId: topicIdValue.trim(),
                    style,
                    description: description.trim(),
                    timeValue,
                    contentFilePath: finalFilePath,
                    contentUrl: finalContentUrl,
                    categoryIds: categoryIdsArray.length > 0 ? categoryIdsArray : null,
                    planIds: planIdsArray.length > 0 ? planIdsArray : null,
                });

                // Sync linked CompanyResource if one exists
                if (existingTopic.companyResourceId) {
                    try {
                        await updateCompanyResource({
                            id: existingTopic.companyResourceId,
                            title: topicIdValue.trim(),
                            description: description.trim() || null,
                            contentFilePath: finalFilePath,
                            contentUrl: finalContentUrl,
                        });
                    } catch (syncErr) {
                        console.error('Failed to sync linked CompanyResource:', syncErr);
                    }
                }
            } else {
                const newTopic = await createTopic({
                    companyId,
                    topicId: topicIdValue.trim(),
                    style,
                    description: description.trim(),
                    timeValue,
                    contentFilePath: finalFilePath,
                    contentUrl: finalContentUrl,
                    categoryIds: categoryIdsArray.length > 0 ? categoryIdsArray : null,
                    planIds: planIdsArray.length > 0 ? planIdsArray : null,
                });

                // Bridge: also save to Company Resources if checkbox is checked
                if (saveToCompanyResources && finalFilePath && newTopic?.id) {
                    try {
                        const resourceCategory = style === 'Poster' ? 'Safety Posters' : 'Training Materials';
                        const fileType = style === 'Video' ? 'Video'
                            : style === 'PDF' ? 'PDF'
                                : style === 'Poster' ? 'Image'
                                    : 'Document';
                        const userId = companyUser?.id || companyOwner?.id || '';
                        const userName = companyUser?.name || companyOwner?.name || '';
                        const newResource = await createCompanyResource({
                            companyId,
                            title: description.trim(),
                            description: null,
                            category: resourceCategory,
                            fileType,
                            contentFilePath: finalFilePath,
                            uploadedByUserId: userId,
                            uploadedByUserName: userName,
                            trainingTopicId: newTopic.id,
                        });
                        // Link back: set companyResourceId on the topic
                        if (newResource?.id) {
                            await updateTrainingTopicGql({ id: newTopic.id, companyResourceId: newResource.id } as any);
                        }
                    } catch (bridgeErr) {
                        console.error('Failed to save to Company Resources:', bridgeErr);
                        // Non-blocking — topic was saved successfully
                    }
                }
            }

            onClose();
        } catch (err: any) {
            console.error('Failed to save topic:', err);
            setSavingError(err?.message || 'Failed to save topic. Please try again.');
        } finally {
            setIsSaving(false);
            setUploadProgress(null);
        }
    }, [
        validate, selectedFile, companyId, contentTab, contentUrl,
        existingFilePath, selectedCategoryIds, isEditing, existingTopic,
        style, description, timeValue, topicIdValue, createTopic, updateTopic, onClose,
        saveToCompanyResources, companyUser, companyOwner,
    ]);

    if (!isOpen) return null;

    return (
        <div className="aetm-overlay" onMouseDown={e => { if (e.target === e.currentTarget) onClose(); }}>
            <div className="aetm-modal" onClick={e => e.stopPropagation()}>
                {/* Header */}
                <div className="aetm-header">
                    <h3>{isEditing ? 'Edit Topic' : 'Add Topic'}</h3>
                    <button className="aetm-close-btn" onClick={onClose} aria-label="Close">
                        ×
                    </button>
                </div>

                {/* Body */}
                <div className="aetm-body">
                    {savingError && (
                        <div className="aetm-error-banner">{savingError}</div>
                    )}

                    {/* Content Section — first so library selection auto-fills fields below */}
                    <div className="aetm-content-section">
                        <p className="aetm-section-label">Content Source</p>
                        <div className="aetm-content-tabs">
                            <button
                                className={`aetm-content-tab ${contentTab === 'upload' ? 'active' : ''}`}
                                onClick={() => setContentTab('upload')}
                            >
                                Upload File
                            </button>
                            <button
                                className={`aetm-content-tab ${contentTab === 'url' ? 'active' : ''}`}
                                onClick={() => setContentTab('url')}
                            >
                                Link / URL
                            </button>
                            <button
                                className={`aetm-content-tab ${contentTab === 'library' ? 'active' : ''}`}
                                onClick={() => setContentTab('library')}
                            >
                                From Library
                            </button>
                        </div>

                        {contentTab === 'upload' ? (
                            <>
                                <div
                                    className={`aetm-dropzone ${isDragOver ? 'drag-over' : ''}`}
                                    onDrop={handleDrop}
                                    onDragOver={handleDragOver}
                                    onDragLeave={handleDragLeave}
                                    onClick={() => fileInputRef.current?.click()}
                                    role="button"
                                    tabIndex={0}
                                    onKeyDown={e => {
                                        if (e.key === 'Enter' || e.key === ' ') {
                                            e.preventDefault();
                                            fileInputRef.current?.click();
                                        }
                                    }}
                                >
                                    <div className="aetm-dropzone-icon">📁</div>
                                    <p className="aetm-dropzone-text">
                                        Drag &amp; drop a file here, or click to browse
                                    </p>
                                    <p className="aetm-dropzone-hint">
                                        MP4, MOV, PDF, JPG, PNG — up to 2 GB
                                    </p>
                                </div>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept={ACCEPTED_MIME_TYPES.join(',')}
                                    style={{ display: 'none' }}
                                    onChange={handleFileInputChange}
                                />

                                {selectedFile && (
                                    <div className="aetm-file-preview">
                                        <span className="aetm-file-preview-name">{selectedFile.name}</span>
                                        <span className="aetm-file-preview-size">
                                            {formatFileSize(selectedFile.size)}
                                        </span>
                                        <button
                                            className="aetm-file-remove-btn"
                                            onClick={handleRemoveFile}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}

                                {!selectedFile && existingFilePath && (
                                    <div className="aetm-existing-file">
                                        📎 Existing file: {existingFilePath.split('/').pop()}
                                        <button
                                            className="aetm-file-remove-btn"
                                            onClick={handleRemoveFile}
                                            title="Remove file"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}

                                {uploadProgress !== null && (
                                    <div className="aetm-upload-progress">
                                        <div className="aetm-upload-progress-bar">
                                            <div
                                                className="aetm-upload-progress-fill"
                                                style={{ width: `${uploadProgress}%` }}
                                            />
                                        </div>
                                        <div className="aetm-upload-progress-text">
                                            Uploading... {uploadProgress}%
                                        </div>
                                    </div>
                                )}

                                {networkWarning && !uploadProgress && (
                                    <div className="aetm-network-warning">
                                        ⚠️ {networkWarning}
                                    </div>
                                )}

                                {errors.file && (
                                    <div className="aetm-field-error">{errors.file}</div>
                                )}

                                {/* Also save to Company Resources checkbox — hidden in template mode */}
                                {!isTemplateMode && !isEditing && (selectedFile || existingFilePath) && (
                                    <label className="aetm-category-item" style={{ marginTop: 12, cursor: 'pointer' }}>
                                        <input
                                            type="checkbox"
                                            checked={saveToCompanyResources}
                                            onChange={e => setSaveToCompanyResources(e.target.checked)}
                                        />
                                        <span className="aetm-category-name">Also save to Company Resources</span>
                                    </label>
                                )}
                            </>
                        ) : contentTab === 'library' ? (
                            <div>
                                <input
                                    className="aetm-url-input"
                                    type="text"
                                    placeholder="Search documents and videos..."
                                    value={librarySearch}
                                    onChange={e => setLibrarySearch(e.target.value)}
                                    style={{ marginBottom: 10, background: '#fff' }}
                                />
                                {libraryLoading ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: 13 }}>
                                        Loading resource library...
                                    </div>
                                ) : filteredLibraryItems.length === 0 ? (
                                    <div style={{ textAlign: 'center', padding: '20px', color: '#94a3b8', fontSize: 13 }}>
                                        {libraryLoaded ? 'No documents or videos found for this company.' : 'Loading...'}
                                    </div>
                                ) : (
                                    <div className="aetm-category-list" style={{ maxHeight: 220 }}>
                                        {filteredLibraryItems.map(item => (
                                            <div
                                                key={item.id}
                                                className="aetm-category-item"
                                                style={{ cursor: 'pointer' }}
                                                onClick={() => handleSelectLibraryItem(item)}
                                            >
                                                <span style={{ fontSize: 16 }}>
                                                    {item.type === 'Video' ? '🎬' : '📄'}
                                                </span>
                                                <span className="aetm-category-name">{item.title}</span>
                                                <span className="aetm-category-target">
                                                    {item.type} {item.folderName ? `· ${item.folderName}` : ''}
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <input
                                className="aetm-url-input"
                                type="text"
                                placeholder="https://example.com/training-video"
                                value={contentUrl}
                                onChange={e => setContentUrl(e.target.value)}
                            />
                        )}
                    </div>

                    {/* Style Selector */}
                    <p className="aetm-section-label">Topic Style</p>
                    <div className="aetm-style-grid">
                        {STYLE_OPTIONS.map(opt => (
                            <div
                                key={opt.value}
                                className={`aetm-style-card ${style === opt.value ? 'selected' : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={() => setStyle(opt.value)}
                                onKeyDown={e => {
                                    if (e.key === 'Enter' || e.key === ' ') {
                                        e.preventDefault();
                                        setStyle(opt.value);
                                    }
                                }}
                            >
                                <span
                                    className="aetm-style-dot"
                                    style={{ background: TOPIC_STYLE_COLORS[opt.value] }}
                                />
                                {opt.label}
                            </div>
                        ))}
                    </div>

                    {style === 'Uncategorized' && (
                        <div className="aetm-uncategorized-note">
                            Uncategorized topics have a time value of zero and don't count toward plan requirements.
                        </div>
                    )}

                    {/* Topic ID + Time Value row */}
                    <div className="aetm-field-row">
                        <div className="aetm-field">
                            <label htmlFor="aetm-topic-id">Topic ID</label>
                            <input
                                id="aetm-topic-id"
                                type="text"
                                value={topicIdValue}
                                onChange={e => setTopicIdValue(e.target.value)}
                                placeholder="e.g. VID-001"
                            />
                            <div className="aetm-field-helper">
                                A short label for this topic (e.g. VID-001, safety-poster-1).
                            </div>
                            {errors.topicId && (
                                <div className="aetm-field-error">{errors.topicId}</div>
                            )}
                        </div>
                        <div className="aetm-field">
                            <label htmlFor="aetm-time-value">Time Value (minutes)</label>
                            <input
                                id="aetm-time-value"
                                type="number"
                                min={0}
                                value={timeValue}
                                onChange={e => setTimeValue(Math.max(0, parseInt(e.target.value) || 0))}
                                disabled={style === 'Uncategorized'}
                            />
                            <div className="aetm-field-helper">
                                Enter 0 for uncategorized topics.
                            </div>
                        </div>
                    </div>

                    {/* Description */}
                    <div className="aetm-field">
                        <label htmlFor="aetm-description">Description</label>
                        <textarea
                            id="aetm-description"
                            value={description}
                            onChange={e => setDescription(e.target.value)}
                            placeholder="Describe this training topic..."
                        />
                        {errors.description && (
                            <div className="aetm-field-error">{errors.description}</div>
                        )}
                    </div>

                    {/* Category Assignment — grouped by plan */}
                    <div className="aetm-categories-section">
                        <p className="aetm-section-label">Assign to Categories</p>
                        <p className="aetm-categories-note">
                            A topic can be assigned to multiple categories. It will count toward the time requirement of each assigned category.
                        </p>
                        {categoriesByPlan.length > 0 ? (
                            <div className="aetm-category-list">
                                {categoriesByPlan.map(({ plan, categories }) => {
                                    const isExpanded = expandedPlanIds.has(plan.id);
                                    const selectedInPlan = categories.filter(c => selectedCategoryIds.has(c.id)).length;
                                    return (
                                        <div key={plan.id} className="aetm-plan-group">
                                            <div
                                                className="aetm-plan-group-header"
                                                onClick={() => togglePlanExpanded(plan.id)}
                                                role="button"
                                                tabIndex={0}
                                                onKeyDown={e => {
                                                    if (e.key === 'Enter' || e.key === ' ') {
                                                        e.preventDefault();
                                                        togglePlanExpanded(plan.id);
                                                    }
                                                }}
                                            >
                                                <span className="aetm-plan-group-arrow">{isExpanded ? '▼' : '▶'}</span>
                                                <span className="aetm-plan-group-name">{plan.name}</span>
                                                <span className={`aetm-plan-group-status ${plan.status.toLowerCase()}`}>
                                                    {plan.status}
                                                </span>
                                                {selectedInPlan > 0 && (
                                                    <span className="aetm-plan-group-count">{selectedInPlan} selected</span>
                                                )}
                                            </div>
                                            {isExpanded && (
                                                <div className="aetm-plan-group-categories">
                                                    {categories.map(cat => {
                                                        const isLocked = lockedCategoryIds.has(cat.id) && selectedCategoryIds.has(cat.id);
                                                        return (
                                                            <label key={cat.id} className={`aetm-category-item${isLocked ? ' locked' : ''}`}>
                                                                <input
                                                                    type="checkbox"
                                                                    checked={selectedCategoryIds.has(cat.id)}
                                                                    onChange={() => toggleCategory(cat.id)}
                                                                    disabled={isLocked}
                                                                />
                                                                <span className="aetm-category-name">{cat.name}</span>
                                                                <span className="aetm-category-target">
                                                                    {cat.targetMinutes} min target
                                                                </span>
                                                                {isLocked && (
                                                                    <span style={{ fontSize: '10px', color: '#94a3b8', marginLeft: 4 }} title="Scheduled in active plan">🔒</span>
                                                                )}
                                                            </label>
                                                        );
                                                    })}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        ) : (
                            <div className="aetm-no-categories">
                                No categories available. Create a plan with categories first.
                            </div>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="aetm-footer">
                    <button className="aetm-btn-cancel" onClick={onClose} disabled={isSaving}>
                        Cancel
                    </button>
                    <button
                        className="aetm-btn-save"
                        onClick={handleSave}
                        disabled={isSaving}
                    >
                        {isSaving ? 'Saving...' : 'Save Topic'}
                    </button>
                </div>
            </div>
        </div>
    );
};
